import { EditOutlined, FileTextOutlined } from '@ant-design/icons'
import { Button, Empty, Form, Select, Space, Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { hasPermission } from '../../config-func'
import { PAGE_SIZE_OPTIONS } from '../../configData'
import {
  bookingSearch,
  bookingUpdateStatus
} from '../../services/booking-management-service'
import Card from '../Card'
import Loading from '../Loading'
import ModalCustom from '../common/Modal/ModalCustom'
import openNotification from '../common/Notification/openNotification '
import TableField from '../common/Table/TableField'
import FilterForm from './FilterForm'
import Note from './Note'
import { BOOKING_STATUS, BOOKING_TYPE } from './configs'

const getUserText = (record, idKey, infoKeys = []) => {
  for (const key of infoKeys) {
    const info = record?.[key]
    if (info?.id || info?.name) {
      return [info?.id || record?.[idKey], info?.name].filter(Boolean).join('-')
    }
  }
  return record?.[idKey] || ''
}

const formatUnixTime = (value) =>
  value ? dayjs.unix(Number(value)).format('DD/MM/YYYY HH:mm:ss') : ''

const BookingManagement = () => {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
  const [formStatus] = Form.useForm()
  const [dataSource, setDataSource] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [isNoteOpen, setIsNoteOpen] = useState(false)
  const [isStatusOpen, setIsStatusOpen] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [filters, setFilters] = useState({
    currentPage: 1,
    pageSize: 10
  })

  const handleSearch = () => {
    if (filters.currentPage === 1) {
      fetchData()
    } else {
      setFilters((prev) => ({ ...prev, currentPage: 1 }))
    }
  }

  async function fetchData() {
    if (
      filters?.date?.[0] &&
      filters?.date?.[1] &&
      filters.date[1].diff(filters.date[0], 'day') > 60
    ) {
      openNotification('error', 'Khoảng thời gian không được vượt quá 60 ngày')
      return
    }

    setLoading(true)

    const params = {
      page: filters.currentPage,
      pageSize: filters.pageSize,
      user_create: filters.user_create || '',
      post_id: filters.post_id || '',
      booker_id: filters.booker_id || '',
      type: filters.type || '',
      status: filters.status || '',
      startTime: filters?.date?.[0]
        ? dayjs(filters.date[0]).startOf('day').unix()
        : '',
      endTime: filters?.date?.[1]
        ? dayjs(filters.date[1]).endOf('day').unix()
        : ''
    }

    const cleanedData = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== '' && value !== null && value !== undefined
      )
    )

    try {
      const response = await bookingSearch(cleanedData)
      const data = response?.data
      const list = Array.isArray(data)
        ? data
        : data?.list || data?.rows || response?.list || []
      const totalData =
        data?.total || data?.totalElements || response?.total || list.length

      setDataSource(list)
      setTotal(totalData)
    } catch (error) {
      openNotification(
        'error',
        error?.response?.data?.message || 'Đã có lỗi xảy ra'
      )
      setDataSource([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters.currentPage, filters.pageSize, filters.reset])

  const handleUpdateStatus = async () => {
    try {
      const values = await formStatus.validateFields()
      const body = {
        ...selectedBooking,
        status: values.status
      }

      const response = await bookingUpdateStatus(body)
      openNotification('success', response?.message || 'Cập nhật thành công')
      setIsStatusOpen(false)
      setSelectedBooking(null)
      formStatus.resetFields()
      fetchData()
    } catch (error) {
      if (error?.errorFields) return
      openNotification(
        'error',
        error?.response?.data?.message || 'Đã có lỗi xảy ra'
      )
    }
  }

  const initialColumns = [
    {
      title: 'STT',
      dataIndex: 'stt',
      key: 'stt',
      fixed: 'left',
      width: 60,
      align: 'center',
      render: (_, __, index) =>
        (filters.currentPage - 1) * filters.pageSize + index + 1
    },
    {
      title: 'Người book',
      dataIndex: 'booker_id',
      key: 'booker_id',
      width: 180,
      render: (_, record) =>
        getUserText(record, 'booker_id', [
          'user_booker_info',
          'booker_info',
          'booker'
        ])
    },
    {
      title: 'Người lên book',
      dataIndex: 'user_create_book',
      key: 'user_create_book',
      width: 180,
      render: (_, record) =>
        getUserText(record, 'user_create_book', [
          'user_create_book_info',
          'user_info',
          'user_create_info',
          'userCreatedInfo',
          'creator_info'
        ])
    },
    {
      title: 'Loại book',
      dataIndex: 'type',
      key: 'type',
      width: 130,
      render: (_, record) => {
        const value = record?.type
        return (
          BOOKING_TYPE.find((item) => item.value == value)?.label || value || ''
        )
      }
    },
    {
      title: 'Mã tin',
      dataIndex: 'post_id',
      key: 'post_id',
      width: 120
    },
    {
      title: 'Hiện trạng',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (value) => {
        const status = BOOKING_STATUS.find((item) => item.value == value)
        return status ? (
          <Tag color={status.color}>{status.label}</Tag>
        ) : (
          'Chưa cập nhật'
        )
      }
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: formatUnixTime
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 170,
      render: formatUnixTime
    },
    {
      title: 'Hành động',
      key: 'action',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          {hasPermission('P86.1', permissions) && (
            <Tooltip title="Cập nhật hiện trạng">
              <Button
                style={{ width: 32 }}
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedBooking(record)
                  formStatus.setFieldsValue({ status: record?.status })
                  setIsStatusOpen(true)
                }}
              />
            </Tooltip>
          )}
          {hasPermission('P86.2', permissions) && (
            <Tooltip title="Xem note">
              <Button
                style={{ width: 32 }}
                icon={<FileTextOutlined />}
                onClick={() => {
                  setSelectedBooking(record)
                  setIsNoteOpen(true)
                }}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ]

  return (
    <div>
      <Loading loading={loading} />
      <Card className="mb-2">
        <FilterForm handleSearch={handleSearch} setFilters={setFilters} />
      </Card>

      <Card>
        <TableField
          loading={loading}
          columns={initialColumns}
          data={dataSource}
          scroll={{ x: 'max-content', y: 'calc(100vh - 400px)' }}
          className="custom-table"
          rowKey={(record, index) => record?.id || index}
          pagination={{
            current: filters.currentPage,
            pageSize: filters.pageSize,
            total,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            showSizeChanger: true,
            onChange: (current, pageSize) => {
              setFilters((prev) => ({
                ...prev,
                currentPage: prev.pageSize !== pageSize ? 1 : current,
                pageSize
              }))
            },
            showTotal: (totalData, range) =>
              `Hiển thị ${range[0]}-${range[1]} của ${totalData} bản ghi`
          }}
          locale={{
            emptyText: <Empty description="Không có dữ liệu" />
          }}
        />
      </Card>

      <ModalCustom
        title="Note booking"
        visible={isNoteOpen}
        centered
        width={760}
        footer={[]}
        onCancel={() => {
          setIsNoteOpen(false)
          setSelectedBooking(null)
        }}
      >
        <Note bookingId={selectedBooking?.id} />
      </ModalCustom>
      <ModalCustom
        title="Cập nhật hiện trạng"
        visible={isStatusOpen}
        centered
        width={520}
        confirmText="Cập nhật"
        cancelText="Huỷ"
        onCancel={() => {
          setIsStatusOpen(false)
          setSelectedBooking(null)
          formStatus.resetFields()
        }}
        onConfirm={handleUpdateStatus}
      >
        <Form form={formStatus} layout="vertical">
          <Form.Item
            label="Hiện trạng"
            name="status"
            rules={[{ required: true, message: 'Vui lòng chọn hiện trạng' }]}
          >
            <Select placeholder="Chọn hiện trạng">
              {BOOKING_STATUS.map((item) => (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </ModalCustom>
    </div>
  )
}

export default BookingManagement
